import Vue from 'vue'
import router from '@/router'
import to from 'await-to-js'
import client from '@/api/client'
import productGroupQuery from '@/api/gql/product/query/product-group.gql'
import productGroupReviewsQuery from '@/api/gql/product/query/product-group-reviews.gql'
import { loadForAtLeast } from '@/helpers/request'
import {
  notifyExternalServices,
  ACTIONS,
} from '@/services/external-communication/external-communication'
import routeNames from '@/router/route-names'

const FIRST_REVIEW_PAGE = 1
const LOW_STOCK_LIMIT = 12
export const REVIEWS_PER_PAGE = 3
export const BEST_REVIEW_RATING = 5
export const WORST_REVIEW_RATING = 1
export const INITIAL_SLIDE_INDEX = 0
export const TOPGALLERY_IMAGE_OPTIONS = {
  format: 'jpg',
  backgroundColor: 'f7f7f7',
}

export const PRODUCT_TYPES = {
  shoes: 'shoes',
  apparel: 'apparel',
  accessories: 'accessories',
}

export const GENDERS = {
  mens: 'mens',
  womens: 'womens',
  unisex: 'unisex',
}

GENDERS.default = GENDERS.mens

/**
 * Returns true if there is stock but is below the stock limit that we have set
 *
 * @param {Number} stock - Stock left for a selected sku
 */
export function isLowStock(stock) {
  return !!(stock && stock <= LOW_STOCK_LIMIT)
}

function redirectToFilterPage(hostname, locale) {
  window.location.href = `${hostname}/${locale}/explore`
}

function findVariantIndex(variants, routeColors) {
  return variants.findIndex(variant => {
    return routeColors.every(routeColor => {
      return variant.color.toLowerCase().indexOf(routeColor) > -1
    })
  })
}

function createRouteColors(colorString) {
  return colorString.toLowerCase().replace(/-?[^0-9a-zA-Z-]+-?/g, '-')
}

function orderVariantsByNewColors(productGroup) {
  let counter = 0
  const orderedVariants = productGroup.productData.variants.reduce((orderedVariants, variant) => {
    if (variant.labels.includes('new-color')) {
      orderedVariants.splice(counter, 0, variant)
      counter++
    } else orderedVariants.push(variant)
    return orderedVariants
  }, [])
  productGroup.productData.variants = orderedVariants
  return productGroup
}

export default {
  namespaced: true,
  state: {
    currentGalleryItemIndex: 0,
    currentVariantIndex: 0,
    hasFetchedFirstReviews: false,
    isAllItemsExpanded: false,
    isFastConnection: true,
    isGalleryOpen: false,
    hasTopGalleryMounted: false,
    hasStickyMenuMounted: false,
    hasTopBannerMounted: false,
    isLoaded: false,
    isLoadingMoreReviews: false,
    productGroup: { productData: { variants: [] } },
    reviews: [],
    reviewRatingAverage: 0,
    reviewTotal: 0,
    reviewPageLoaded: 0,
    scrollPosition: 0,
    selectedGender: GENDERS.default,
    slideIndex: INITIAL_SLIDE_INDEX,
    slideResetter: undefined,
    recommendationSettings: {
      color: undefined,
      position: undefined,
    },
    isSizeInfoVisible: false,
  },

  mutations: {
    addReviews(state, payload) {
      state.reviews.push(...payload)
      state.reviewPageLoaded++
    },

    openGallery(state, payload) {
      state.isGalleryOpen = payload
    },

    setTopGalleryMounted(state, payload) {
      state.hasTopGalleryMounted = payload
    },

    setTopBannerMounted(state, payload) {
      state.hasTopBannerMounted = payload
    },

    setStickyMenuMounted(state, payload) {
      state.hasStickyMenuMounted = payload
    },

    setAllItemsExpanded(state, payload) {
      state.isAllItemsExpanded = payload
    },

    setConnectionSpeed(state, payload) {
      state.isFastConnection = payload
    },

    setCurrentCaptionIndex(state, payload) {
      state.lastCaptionIndex = state.currentCaptionIndex
      state.currentCaptionIndex = payload
    },

    setCurrentGalleryItemIndex(state, payload) {
      state.currentGalleryItemIndex = payload
    },

    setCurrentVariantIndex(state, payload) {
      state.currentVariantIndex = payload
    },

    setGender(state, payload) {
      state.selectedGender = payload
    },

    setHasFetchedFirstReviews(state, payload) {
      state.hasFetchedFirstReviews = payload
    },

    setIsLoadingMoreReviews(state, payload) {
      state.isLoadingMoreReviews = payload
    },

    setProductGroup(state, payload) {
      state.productGroup = payload
      state.isLoaded = true
    },

    setReviewRatingAverage(state, payload) {
      state.reviewRatingAverage = payload
    },

    setReviews(state, payload) {
      state.reviews = payload
      state.reviewPageLoaded = FIRST_REVIEW_PAGE
    },

    setReviewTotal(state, payload) {
      state.reviewTotal = payload
    },

    setScrollposition(state, payload) {
      state.scrollPosition = payload
    },

    setSlideIndex(state, payload) {
      state.slideIndex = payload
    },

    resetSlideIndex(state) {
      // To prevent the slide animation on reset
      // i.e. [3, false] would go to index 3 without animation
      // See vue-carousel documentation of `navigateTo` for details
      state.slideResetter = [INITIAL_SLIDE_INDEX, false]
    },

    editRecommendationSettings(state, payload) {
      state.recommendationSettings = {
        color: payload.color || state.recommendationSettings.color,
        position: payload.position || state.recommendationSettings.position,
      }
    },

    setSizeInfo(state, payload) {
      state.isSizeInfoVisible = payload
    },
  },

  actions: {
    replaceRoute({ rootState, state, getters }, isVariantClick = true) {
      router.replace({
        name: routeNames.product,
        params: {
          ...rootState.route.params,
          gender: state.selectedGender,
          colors: getters.selectedColors,
          isVariantClick,
        },
      })
    },

    validateRoute({ rootState, getters }) {
      const routeColors = rootState.route.params.colors?.split('-') || []
      const isValidColors = findVariantIndex(getters.product.variants, routeColors) > -1
      const isValidGender = Object.values(GENDERS).includes(rootState.route.params.gender)

      if (!isValidColors || !isValidGender) {
        // Fallback to main variant which is the first male variant
        router.replace({
          name: routeNames.product,
          query: rootState.route.query,
          params: { isVariantClick: true },
        })
      }
    },

    handleGenderSwitch({ state, getters, commit, dispatch }, gender) {
      if (gender === state.selectedGender) return
      commit('setGender', gender)
      const currentVariantIndex =
        gender === GENDERS.mens ? getters.firstMaleVariantIndex : getters.firstFemaleVariantIndex
      commit('setCurrentVariantIndex', currentVariantIndex)
      commit('resetSlideIndex')
      dispatch('replaceRoute')
      notifyExternalServices({
        action: ACTIONS.productInteraction,
        details: { action: 'gender-select', label: gender },
      })
    },

    async fetchData({ rootState, commit, dispatch, getters }) {
      const [error, result] = await to(
        client.query({
          query: productGroupQuery,
          variables: {
            slug: getters.productGroupSlug,
          },
        })
      )
      if (error || !result.data?.productGroup?.productData?.variants.length)
        return redirectToFilterPage(rootState.page.hostname, rootState.page.locale)

      const { productGroup, sizeInfo: { isSizeInfoVisible = false } = {} } = result.data
      commit('setReviewRatingAverage', productGroup.reviewRatingAverage)
      commit('setReviewTotal', productGroup.reviewTotal)
      delete productGroup.reviewRatingAverage
      delete productGroup.reviewTotal
      commit('setProductGroup', orderVariantsByNewColors(productGroup))
      if (getters.isRaffle) dispatch('page/startUpdatingCurrentTime', null, { root: true })
      commit('setSizeInfo', isSizeInfoVisible)
      notifyExternalServices({
        action: ACTIONS.sizeInfo,
        details: { isSizeInfoVisible },
      })
      dispatch('validateRoute')
    },

    async fetchReviews({ commit, getters }) {
      const {
        data: { productGroup },
      } = await client.query({
        query: productGroupReviewsQuery,
        variables: {
          slug: getters.productGroupSlug,
        },
      })
      commit('setHasFetchedFirstReviews', true)

      if (productGroup) {
        Vue.nextTick(() => commit('setReviews', productGroup.reviews))
      }
    },

    async loadMoreReviews({ state, commit, getters }) {
      commit('setIsLoadingMoreReviews', true)
      const {
        data: { productGroup },
      } = await loadForAtLeast(
        client.query({
          query: productGroupReviewsQuery,
          variables: {
            slug: getters.productGroupSlug,
            reviewPage: state.reviewPageLoaded + 1,
          },
        })
      )
      commit('setIsLoadingMoreReviews', false)
      commit('addReviews', productGroup.reviews)
    },
  },

  getters: {
    currentVariant: (state, getters) => {
      return {
        productName: getters.productName,
        ...getters.product.variants[state.currentVariantIndex],
      }
    },
    currentHighlights: (state, getters) =>
      state.isLoaded ? getters.currentVariant.highlights : null,
    currentSpreeProduct: (state, getters) =>
      state.isLoaded ? getters.currentVariant.spreeProduct : null,
    firstMaleVariantIndex: (state, getters) =>
      getters.product.variants.findIndex(variant => variant.gender === GENDERS.mens),
    firstFemaleVariantIndex: (state, getters) =>
      getters.product.variants.findIndex(variant => variant.gender === GENDERS.womens),
    isVisible: (state, getters) => state.isLoaded && getters.hasLazyComponentsMounted,
    mainVariant: (_state, getters) => getters.product.variants[getters.firstMaleVariantIndex] || 0,
    mainVariantColors: (state, getters) =>
      state.isLoaded ? createRouteColors(getters.mainVariant.color) : '',
    product: state => state.productGroup.productData,
    productName: (_state, getters) => getters.product.name,
    productGroupSlug: (_state, _getters, rootState) => rootState.route.params.productGroup,
    selectedColors: (state, getters) =>
      state.isLoaded ? createRouteColors(getters.currentVariant.color) : '',
    hasLazyComponentsMounted: (state, getters) =>
      state.hasTopGalleryMounted &&
      state.hasStickyMenuMounted &&
      getters.hasDynamicComponentsMounted,
    hasDynamicComponentsMounted: (state, getters) =>
      getters.isRaffle && !getters.hasRaffleCountdownEnded ? state.hasTopBannerMounted : true,
    isRaffle: (state, getters) => !!getters.currentSpreeProduct?.isRaffle,
    topBannerImageUrlMobile: (state, getters) => getters.getTopBannerUrl('mediaMobileUrl'),
    topBannerImageUrlDesktop: (state, getters) => getters.getTopBannerUrl('mediaDesktopUrl'),
    topBannerVideoUrlMobile: (state, getters) => getters.getTopBannerUrl('videoMobileUrl'),
    topBannerVideoUrlDesktop: (state, getters) => getters.getTopBannerUrl('videoDesktopUrl'),
    getTopBannerUrl: (state, getters) => urlPropName =>
      getters.currentVariant?.topBanner?.[urlPropName] || '',
    raffleExpiresAt: (state, getters) => getters.currentSpreeProduct?.raffleExpiresAt || '',
    hasRaffleCountdownEnded: (state, getters, rootState) =>
      Date.parse(getters.raffleExpiresAt) - rootState.page.currentTime < 0,
  },
}
